import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';

import {
  applyForCredit,
  resetCreditApplication,
  lookupCompany,
  resetCompanyLookup,
} from '../../actions/creditTerms.action';
import {EMAIL_REGEX, OUTCOME_MESSAGE_CODES} from '../../constants/constants';
import {LOGIN_PATH, SIGNUP_PATH} from '../../constants/routePaths';
import Loading from '../../components/Loading/Loading';
import {PostNode, MainTitle} from '../BlogPage/BlogPage.style';
import {
  Intro,
  Benefits,
  Eligibility,
  Panel,
  Field,
  Label,
  Hint,
  Input,
  ConsentRow,
  Disclaimer,
  Button,
  Message,
  ErrorText,
  Links,
  LookupResult,
  CompanyName,
  CompanyAddress,
  StatusBadge,
  LookupHint,
} from './CreditTermsPage.Style';

// The full outcome text is emailed to the applicant by credit-check-svc; the
// page only shows a brief confirmation of the outcome. NOT_FOUND is an input
// error (not emailed) and keeps its on-page retry prompt.
const OUTCOME_MESSAGES = {
  [OUTCOME_MESSAGE_CODES.DECLINED]: {
    variant: 'declined',
    text:
      'Thank you for applying for a trade credit account with JJ Foodservice.\n\n' +
      'Following an automated assessment using information from independent business ' +
      'information providers, we are unable to offer a trade credit facility on this occasion. ' +
      'This is not a reflection of your business relationship with JJ Foodservice, and you are ' +
      'welcome to continue trading with us on our standard payment terms.\n\n' +
      'If you believe this outcome may be incorrect or would like to discuss it, please contact ' +
      'our Credit Control team on 01992 701745.',
  },
  [OUTCOME_MESSAGE_CODES.NOT_FOUND]: {
    variant: 'notfound',
    text:
      "We couldn't find a company matching that Companies House number.\n\n" +
      'Please check the number is correct and try again, or contact our Credit Control team on ' +
      '01992 701745 or creditcontrol@jjfoodservice.com for help.',
  },
};

const Marketing = () => (
  <>
    <MainTitle>Apply for monthly credit terms</MainTitle>
    <Intro>
      Spread the cost of your stock with a JJ Foodservice monthly credit
      account. Order through the month and settle a single statement. All
      invoices for a calendar month become due by the 15th of the following
      month.
    </Intro>
    <Benefits>
      <li>One consolidated monthly statement instead of paying per order</li>
      <li>Up to 6 weeks to pay, improving your cashflow</li>
    </Benefits>
  </>
);

const CreditTermsPage = () => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const isLoggedIn = Boolean(auth && auth.access_token);
  const loading = useSelector(state => state.loading.creditApplication);
  const error = useSelector(
    state => state.errors && state.errors.creditApplication
  );
  const outcome = useSelector(
    state => state.creditTerms && state.creditTerms.outcome
  );
  const companyLookup = useSelector(
    state => state.creditTerms && state.creditTerms.companyLookup
  );
  const responseWorkingDays = useSelector(
    state => state.creditTerms && state.creditTerms.responseWorkingDays
  );

  const [companiesHouseNumber, setCompaniesHouseNumber] = useState('');
  const [directorEmail, setDirectorEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [
    requestedMonthlyCreditLimit,
    setRequestedMonthlyCreditLimit,
  ] = useState('');
  const [consents, setConsents] = useState({
    authorised: false,
    terms: false,
    tradeCreditTerms: false,
    creditReference: false,
    privacy: false,
  });

  // Clear any outcome/error left in the store from a previous visit so the
  // form is shown afresh on mount.
  useEffect(() => {
    dispatch(resetCreditApplication());
  }, [dispatch]);

  // A Companies House number is exactly 8 characters. Once the user has entered
  // a full-length number, look it up (the saga debounces) so they can confirm
  // the company; clear the panel whenever the number is incomplete.
  useEffect(() => {
    if (companiesHouseNumber.length === 8) {
      dispatch(lookupCompany(companiesHouseNumber));
    } else {
      dispatch(resetCompanyLookup());
    }
  }, [companiesHouseNumber, dispatch]);

  const toggleConsent = key =>
    setConsents(prev => ({...prev, [key]: !prev[key]}));

  const allConsented = Object.values(consents).every(Boolean);
  // Only flag it once the user has left the field (blurred) — checking on
  // every keystroke flags a still-incomplete email as an error before
  // they've even finished typing it.
  const emailInvalid =
    emailTouched &&
    directorEmail.trim().length > 0 &&
    !EMAIL_REGEX.test(directorEmail.trim());
  // Whole pounds only.
  const requestedCreditLimit = parseInt(requestedMonthlyCreditLimit, 10);
  const canSubmit =
    companiesHouseNumber.trim().length > 0 &&
    EMAIL_REGEX.test(directorEmail.trim()) &&
    requestedCreditLimit > 0 &&
    allConsented &&
    !loading;

  const handleSubmit = e => {
    e.preventDefault();
    if (!canSubmit) return;
    dispatch(
      applyForCredit({
        companiesHouseNumber: companiesHouseNumber.trim(),
        directorEmail: directorEmail.trim(),
        requestedMonthlyCreditLimit: requestedCreditLimit,
      })
    );
  };

  const renderOutcome = () => {
    if (outcome === OUTCOME_MESSAGE_CODES.REFERRED) {
      // The response time is score-based (2 or 5 working days), returned by
      // credit-check-svc; fall back to 2 if it is somehow absent.
      const days = responseWorkingDays || 2;
      const text =
        'Your application has been passed onto our Credit Control team for final review.\n\n' +
        `We aim to contact you with the outcome of your application within ${days} working days.\n\n` +
        'If you have any questions in the meantime, please contact our Credit Control team on 01992 701745.';
      return <Message variant="referred">{text}</Message>;
    }
    const message =
      OUTCOME_MESSAGES[outcome] ||
      OUTCOME_MESSAGES[OUTCOME_MESSAGE_CODES.NOT_FOUND];
    return <Message variant={message.variant}>{message.text}</Message>;
  };

  return (
    <PostNode>
      <Marketing />

      <Eligibility>
        To be eligible for a JJ Foodservice trade credit account, your business
        should spend, or intend to spend, a minimum of £2,000 per month with JJ
        Foodservice. If your anticipated monthly spend is below this level, a
        trade credit account may not be available at this time. However, you are
        still welcome to trade with us using our standard payment methods.
      </Eligibility>

      {!isLoggedIn && (
        <Panel>
          <Label>Log in to apply</Label>
          <Intro>
            You need to be logged in to a JJ Foodservice account to apply for
            credit. Please log in or create an account to continue.
          </Intro>
          <Links>
            <Link to={LOGIN_PATH}>Log in</Link>
            <Link to={SIGNUP_PATH}>Create an account</Link>
          </Links>
        </Panel>
      )}

      {isLoggedIn && outcome && renderOutcome()}

      {isLoggedIn && !outcome && (
        <Panel as="form" onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="companiesHouseNumber">Companies House number</Label>
            <Input
              id="companiesHouseNumber"
              type="text"
              value={companiesHouseNumber}
              onChange={e =>
                // A Companies House number is at most 8 characters; strip
                // spaces/punctuation and cap length so it can't be rejected
                // upstream (credit-check-svc zero-pads short numeric values).
                setCompaniesHouseNumber(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                    .slice(0, 8)
                )
              }
              maxLength={8}
              placeholder="e.g. 02330996"
              autoComplete="off"
            />
            {companyLookup && companyLookup.loading && (
              <LookupHint>Checking Companies House…</LookupHint>
            )}
            {companyLookup && companyLookup.found && (
              <LookupResult>
                <CompanyName>{companyLookup.companyName}</CompanyName>
                {companyLookup.companyStatus && (
                  <StatusBadge
                    active={companyLookup.companyStatus === 'active'}
                  >
                    {companyLookup.companyStatus.replace(/-/g, ' ')}
                  </StatusBadge>
                )}
                {companyLookup.registeredAddress && (
                  <CompanyAddress>
                    {companyLookup.registeredAddress}
                  </CompanyAddress>
                )}
              </LookupResult>
            )}
            {companyLookup &&
              !companyLookup.loading &&
              !companyLookup.found &&
              !companyLookup.error &&
              companiesHouseNumber.length === 8 && (
                <LookupHint>
                  No company found for that Companies House number. Please check
                  it is correct.
                </LookupHint>
              )}
            {companyLookup && companyLookup.error && (
              <LookupHint variant="error">
                We couldn&apos;t check that company right now. You can still
                submit your application.
              </LookupHint>
            )}
          </Field>
          <Field>
            <Label htmlFor="directorEmail">Director's email address</Label>
            <Input
              id="directorEmail"
              type="email"
              value={directorEmail}
              onChange={e => setDirectorEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="director@company.co.uk"
              autoComplete="off"
              $hasError={emailInvalid}
            />
            {emailInvalid && <ErrorText>Invalid email address</ErrorText>}
          </Field>
          <Field>
            <Label htmlFor="requestedMonthlyCreditLimit">
              Requested monthly credit limit (£)
            </Label>
            <Hint>Based on the expected spend over a 30-day period.</Hint>
            <Input
              id="requestedMonthlyCreditLimit"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={requestedMonthlyCreditLimit}
              onChange={e => setRequestedMonthlyCreditLimit(e.target.value)}
              placeholder="e.g. 5000"
              autoComplete="off"
            />
          </Field>

          <ConsentRow>
            <input
              type="checkbox"
              checked={consents.authorised}
              onChange={() => toggleConsent('authorised')}
            />
            <span>
              I confirm I am authorised to apply for credit on behalf of the
              company.
            </span>
          </ConsentRow>
          <ConsentRow>
            <input
              type="checkbox"
              checked={consents.terms}
              onChange={() => toggleConsent('terms')}
            />
            <span>
              I agree to JJ Foodservice{' '}
              <Link
                to="/content/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </Link>
              .
            </span>
          </ConsentRow>
          <ConsentRow>
            <input
              type="checkbox"
              checked={consents.tradeCreditTerms}
              onChange={() => toggleConsent('tradeCreditTerms')}
            />
            <span>
              I agree to the JJ Foodservice{' '}
              <Link
                to="/content/trade-credit-account-terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Trade Credit Account Terms &amp; Conditions
              </Link>
              .
            </span>
          </ConsentRow>
          <ConsentRow>
            <input
              type="checkbox"
              checked={consents.creditReference}
              onChange={() => toggleConsent('creditReference')}
            />
            <span>
              I authorise JJ Foodservice to obtain information from credit
              reference and business information providers for the purpose of
              assessing this application.
            </span>
          </ConsentRow>
          <ConsentRow>
            <input
              type="checkbox"
              checked={consents.privacy}
              onChange={() => toggleConsent('privacy')}
            />
            <span>
              I have read and understood the JJ Foodservice{' '}
              <Link
                to="/content/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </span>
          </ConsentRow>

          <Disclaimer>
            JJ Foodservice will use information obtained from independent
            business information providers to assess this application. This
            assessment may include automated decision-making.
          </Disclaimer>

          {loading ? (
            <Loading isLight={false} />
          ) : (
            <Button type="submit" disabled={!canSubmit}>
              Apply for credit
            </Button>
          )}
          {error && (
            <ErrorText>
              {typeof error === 'string'
                ? error
                : 'Sorry, we could not process your application right now. Please try again shortly.'}
            </ErrorText>
          )}
        </Panel>
      )}
    </PostNode>
  );
};

export default CreditTermsPage;
